<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, useSlots } from 'vue'
import CardTextIn from '@/components/molecules/CardTextIn/CardTextIn.vue'
import Carousel from '@/components/molecules/Carousel/Carousel.vue'
import { SwiperSlide } from 'swiper/vue'
import type { AutoplayOptions } from 'swiper/types'
import ButtonV2 from '~/components/atoms/ButtonV2/ButtonV2.vue'
import type { ButtonProps } from '~/components/atoms/ButtonV2'
import type { TVComponent } from '~/types/tv'
import { CarouselHorizontalStyles } from './CarouselControlsHorizontal.styles'
import type { CardGeneric } from '~/types/cards'

type TVCarousel = TVComponent<typeof CarouselHorizontalStyles>

export interface CarouselControlsProps {
  classNames?: TVCarousel['slots'] // Permite injetar classes externas
  showArrow?: boolean
  bgColor?: string
  backgroundColor?: string
  items?: CardGeneric[]
  title: string
  button?: ButtonProps
  customBreakpoints?: Record<number, { slidesPerView: number | 'auto'; spaceBetween: number }>
  showNavigation?: boolean
  infinite?: boolean
  autoplay?: boolean | AutoplayOptions
  hasGhostSlide?: boolean
  dotsClass?: string
  dotsBackgroundColor?: string
  dotsColor?: string
  dotsActiveColor?: string
}

const props = withDefaults(defineProps<CarouselControlsProps>(), {
  bgColor: 'bg-foundation-laranja',
  showArrow: true,
  showNavigation: false,
  autoplay: false,
  hasGhostSlide: false
})

const slots = useSlots()
const carouselRef = ref<InstanceType<typeof Carousel>>()
const isBeginning = ref(true)
const isEnd = ref(false)

const updateNavState = (swiper: any) => {
  isBeginning.value = swiper.isBeginning
  if (props.hasGhostSlide && swiper.params?.slidesPerView && swiper.params.slidesPerView !== 'auto') {
    isEnd.value = swiper.activeIndex >= swiper.slides.length - Math.ceil(Number(swiper.params.slidesPerView))
  } else {
    isEnd.value = swiper.isEnd
  }
}

const onSwiper = (swiper: any) => updateNavState(swiper)

const onSlideChange = () => {
  if (carouselRef.value?.swiper) updateNavState(carouselRef.value.swiper)
}

const rootStyle = computed(() => (props.backgroundColor ? { backgroundColor: props.backgroundColor } : undefined))

const DESKTOP_BREAKPOINT = 1024
const isDesktop = ref(false)

const updateIsDesktop = () => {
  if (typeof window !== 'undefined') {
    isDesktop.value = window.innerWidth >= DESKTOP_BREAKPOINT
  }
}

onMounted(() => {
  updateIsDesktop()
  window.addEventListener('resize', updateIsDesktop)
})

onUnmounted(() => {
  window.removeEventListener('resize', updateIsDesktop)
})

const carouselBreakpoints = computed(
  () =>
    props.customBreakpoints ?? {
      0: { slidesPerView: 1.2, spaceBetween: 16 },
      425: { slidesPerView: 1.4, spaceBetween: 16 },
      640: { slidesPerView: 2, spaceBetween: 16 },
      1024: { slidesPerView: 3, spaceBetween: 16 },
      1440: { slidesPerView: 3, spaceBetween: 16 }
    }
)

// Inicia a factory de estilos
const styles = computed(() => CarouselHorizontalStyles())
</script>

<template>
  <section :style="rootStyle" :class="styles.root({ class: [props.bgColor, props.classNames?.root] })">
    <div :class="styles.headerContainer({ class: props.classNames?.headerContainer })">
      <h1 :class="styles.title({ class: props.classNames?.title })">{{ props.title }}</h1>

      <ButtonV2 v-if="props.button" v-bind="props.button" :class="styles.button({ class: props.classNames?.button })">
      </ButtonV2>

      <div v-if="props.showNavigation" :class="styles.navContainer({ class: props.classNames?.navContainer })">
        <button
          type="button"
          aria-label="Slide anterior"
          :disabled="isBeginning"
          :class="styles.navButton({ class: props.classNames?.navButton })"
          @click="carouselRef?.slidePrev()"
        >
          <Icon name="our:arrows-arrow-left" :size="16" :class="styles.navIcon({ class: props.classNames?.navIcon })" />
        </button>
        <button
          type="button"
          aria-label="Próximo slide"
          :disabled="isEnd"
          :class="styles.navButton({ class: props.classNames?.navButton })"
          @click="carouselRef?.slideNext()"
        >
          <Icon
            name="our:arrows-arrow-right"
            :size="16"
            :class="styles.navIcon({ class: props.classNames?.navIcon })"
          />
        </button>
      </div>
    </div>

    <div :class="styles.carouselContainer({ class: props.classNames?.carouselContainer })">
      <Carousel
        ref="carouselRef"
        :custom-breakpoints="carouselBreakpoints"
        :show-arrow="props.showArrow"
        :show-dots="(props.items?.length ?? 0) > 1 || slots.default !== undefined"
        :infinite="props.infinite ?? ((props.items?.length ?? 0) > 1 || slots.default !== undefined)"
        :autoplay="props.autoplay"
        :has-ghost-slide="props.hasGhostSlide"
        :dots-class="props.dotsClass"
        :dotsBackgroundColor="dotsBackgroundColor"
        :dotsColor="dotsColor"
        :dotsActiveColor="dotsActiveColor"
        @swiper="onSwiper"
        @slide-change="onSlideChange"
      >
        <SwiperSlide
          v-for="(item, index) in props.items"
          :key="`${item.title}-${index}`"
          :class="styles.card({ class: props.classNames?.swiper })"
        >
          <CardTextIn
            :image="item.image?.url"
            :image-alt="item.title"
            :image-props="{ sizes: '282px' }"
            :title="item.title"
            :description="item.description"
            :show-favorite="false"
            :class-names="{ title: 'text-3xl! text-neutral-6 font-bold', description: 'typo-p1' }"
            :class="styles.card({ class: props.classNames?.card })"
          />
        </SwiperSlide>

        <SwiperSlide
          v-if="props.hasGhostSlide"
          aria-hidden="true"
          :class="styles.ghostSlide({ class: props.classNames?.ghostSlide })"
        />
      </Carousel>
    </div>
  </section>
</template>
